import styled, {css, keyframes} from 'styled-components';
import {
  actionColor,
  borderRadius,
  errorColor,
  successColor,
  fontNormal,
  fontNormalBig,
  gray20,
  gray80,
  gray95,
  lineHeight,
  spacing,
  white,
} from '@propellerads/stylevariables';
import {BUTTON_TYPE, BUTTON_SIZE, THEME} from './types';

interface StyledButtonProps {
    isDisabled: boolean
    isLoading: boolean
    styledType: BUTTON_TYPE
    isFullWidth: boolean
    size: BUTTON_SIZE
    theme: THEME
}

const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

const StyledButton = styled.button.attrs((props: StyledButtonProps) => ({
  disabled: props.isDisabled || props.isLoading,
}))`
    display: inline-flex;
    align-items: center;
    font-weight: 400;
    cursor: pointer;
    height: ${lineHeight}px;
    line-height: ${lineHeight}px;
    font-size: ${fontNormal}px;
    padding: 0 ${spacing * 4}px;
    justify-content: center;
    white-space: nowrap;
    user-select: none;
    border: 0;
    border-radius: ${borderRadius}px;
    transition: background-color 0.1s linear 0s, border-color 0.1s linear 0s;
    outline: none;
      
    ${(props: StyledButtonProps) => props.styledType === BUTTON_TYPE.PRIMARY && css`
        background-color: ${props.theme?.actionColor?.main || actionColor};
        color: ${white};
        
        &:hover {
            background-color: ${props.theme?.actionColor?.hover || 'hsl(210, 100%, 60%)'};
            color: ${white};
        }
        
        &:active {
            background-color: ${props.theme?.actionColor?.active || 'hsl(210, 100%, 45%)'};
            color: ${white};
        }
    `}

    ${(props: StyledButtonProps) => props.styledType === BUTTON_TYPE.SECONDARY && css`
        background-color: ${white};
        color: ${gray20};
        box-shadow: inset 0 0 0 1px ${gray80};
        
        &:hover {
            background-color: hsl(0, 0%, 90%);
            box-shadow: inset 0 0 0 1px grey;
            color: black;
        }
        
        &:active {
            background-color: hsl(0, 0%, 95%);
            color: ${gray20};
        }
    `}
    
    ${(props) => props.styledType === BUTTON_TYPE.ADVANCED && css`
        background-color: white;
        color: ${props.theme.actionColor?.main || actionColor};
        box-shadow: inset 0 0 0 1px ${props.theme?.actionColor?.main || actionColor};
      
        &:hover {
          box-shadow: inset 0 0 0 1px hsl(210, 100%, 45%);
        }
        
        &:active {
          box-shadow: inset 0 0 0 1px hsl(210, 100%, 40%);
        }
    `}
    
    ${(props) => props.styledType === BUTTON_TYPE.LINK && css`
        background-color: transparent;
        color: ${actionColor};
        line-height: inherit;
        height: auto;
        padding: 0;
        border: 0;
     
        &:hover {
            color: hsl(210, 100%, 20%); 
        }
        
        &:disabled {
            background-color: transparent !important;
        }
    `}
    
    ${(props) => props.styledType === BUTTON_TYPE.PLAIN && css`
        height: auto;
        padding: 0;
        background: transparent;
        border-radius: 0;
        line-height: 1;
        font-weight: normal;
      
        &:hover {
        }
        
        &:active {
        }
        
        &:disabled {
            background-color: transparent !important;
        }
    `}
    
    ${(props) => props.styledType === BUTTON_TYPE.DANGER && css`
        background-color: ${props.theme?.errorColor?.main || errorColor};
        color: ${white};
    
        &:hover {
          background-color: ${props.theme?.errorColor?.hover || 'hsl(360, 85%, 40%)'};
        }
        
        &:active {
          background-color: ${props.theme?.errorColor?.active || 'hsl(360, 85%, 40%)'};
        }
    `}
    
    ${(props) => props.styledType === BUTTON_TYPE.SUCCESS && css`
        background-color: ${props.theme?.successColor?.main || successColor};
        color: ${white};
        
        &:hover {
            background-color: ${props.theme?.successColor?.hover || 'hsl(150, 100%, 25%)'};
            color: ${white};
        }
        
        &:active {
            background-color: ${props.theme?.successColor?.active || 'hsl(150, 100%, 25%)'};
            color: ${white};
        }
    `}
    
    ${(props: StyledButtonProps) => props.size === BUTTON_SIZE.BIG && css`
        height: ${lineHeight * 2}px;
        line-height: ${lineHeight * 2}px;
        padding: 0 24px;
        font-size: ${fontNormalBig}px;
    `}
    
    ${(props) => props.isLoading && css`
        cursor: progress !important;
        position: relative;
        color: transparent;
        
        &:before {
            content: '';
            position: absolute;
            height: 20px;
            width: 20px;
            border: 3px solid;
            border-color: white ${gray80} ${gray80};
            opacity: 0.7;
            border-radius: 50%;
            animation: ${spin} 0.5s linear infinite;
            margin-left: auto;
            margin-right: auto;
        }
        
        &:hover {
             color: transparent;
        } 
    `}
    
    ${(props) => props.isDisabled && css`
        cursor: not-allowed !important;
        background-color: ${gray95} !important;
        box-shadow: none !important;;
        color: gray !important;
        
        &:hover {
            box-shadow: none !important;;
        }
    `}

    ${(props: StyledButtonProps) => props.isFullWidth && css`
        width: 100%;
    `}
`;

export {StyledButton};
